// frontend/src/components/FinanceReport/Accounting/AccountingPage.jsx

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import SearchPanel from "./components/SearchPanel";
import AccountingTabs from "./components/AccountingTabs";
import CurrenciesTab from "./components/currencies/CurrenciesTab";
import AccountsTab from "./components/accounts/AccountsTab";
import FinanceReportTab from "./components/finance-report/FinanceReportTab";
import PaymentDocumentsModal from "./components/PaymentDocumentsModal";
import { authFetch } from "../../api/api"; 

import styles from "./AccountingPage.module.css";

const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;
const API_URL = `${BASE_URL}/api/accounting/accounting-records/`;
const CURRENCIES_URL = `${BASE_URL}/api/accounting/currencies/`;
const PROFIT_ALLOCATIONS_URL = `${BASE_URL}/api/accounting/profit-allocations/`;
const SUPPLIER_PREPAYMENTS_URL = `${BASE_URL}/api/accounting/supplier-prepayments/`;

const EPS = 0.01;

async function fetchAllPages(endpoint) {
  let url = endpoint;
  const results = [];
  const visited = new Set();

  while (url && !visited.has(url)) {
    visited.add(url);

    const response = await authFetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    if (Array.isArray(data)) {
      results.push(...data);
      break;
    }

    results.push(...(data.results || []));

    if (!data.next) break;

    const next = new URL(data.next, `${BASE_URL}/`);
    url = `${BASE_URL}${next.pathname}${next.search}`;
  }

  return results;
}

const formatMoney = (value, currency) => {
  if (value === "" || value === null || value === undefined) return "";
  const symbol = currency?.symbol || "";
  const num = Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return symbol ? `${symbol} ${num}` : num;
};

const formatCNY = (value) => formatMoney(value, { symbol: "¥" });

const formatAllocations = (allocations = []) => {
  if (!Array.isArray(allocations)) return "";
  return allocations
    .map((allocation) => {
      const orderCode =
        allocation.order_code ??
        allocation.order_number ??
        allocation.order?.code ??
        allocation.order?.order_code ??
        allocation.order?.number ??
        allocation.code;
      const amount =
        allocation.allocated_amount ??
        allocation.money_sum ??
        allocation.amount ??
        allocation.sum;
      if (!orderCode) return null;
      return `${orderCode}: ${formatCNY(amount)}`;
    })
    .filter(Boolean)
    .join("; ");
};

const emptyRow = {
  client: "",
  client_name: "",
  date: "",
  payment_agent: "",
  money_sum: "",
  currency: "",
  purpose: "",
  comment: "",
  payment_status: "PENDING",
};

export default function AccountingPage() {
  console.log("AccountingPage mounted");

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ← читаем tab из URL, до объявления activeTab
  const tabFromUrl = searchParams.get("tab") || "accounting";

  const [activeTab, setActiveTab] = useState(tabFromUrl);

  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState(emptyRow);

  const [searchField, setSearchField] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [rowToEdit, setRowToEdit] = useState(null);
  const [editingRow, setEditingRow] = useState(null);

  const [documentsPayment, setDocumentsPayment] = useState(null);


  const [profitAllocations, setProfitAllocations] = useState([]);
  const [supplierPrepayments, setSupplierPrepayments] = useState([]);
  const [distributionError, setDistributionError] = useState("");

  const currenciesById = useMemo(() => {
    const map = {};
    currencies.forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [currencies]);

  const profitByAllocation = useMemo(() => {
    const map = {};
    profitAllocations.forEach((item) => {
      const key = String(item.allocation);
      map[key] = (map[key] || 0) + Number(item.amount || 0);
    });
    return map;
  }, [profitAllocations]);

  const supplierByAllocation = useMemo(() => {
    const map = {};
    supplierPrepayments.forEach((item) => {
      const key = String(item.allocation);
      map[key] = (map[key] || 0) + Number(item.amount || 0);
    });
    return map;
  }, [supplierPrepayments]);

  const getPaymentDistribution = (row) => {
    const moneySum = Number(row.money_sum || 0);
    const paymentRemaining =
      row.unallocated_sum != null
        ? Number(row.unallocated_sum)
        : moneySum - Number(row.allocated_sum || 0);

    const allocations = Array.isArray(row.allocations) ? row.allocations : [];
    const incompleteAllocations = [];

    allocations.forEach((allocation) => {
      const allocationId = String(allocation.id);
      const amount = Number(allocation.amount || 0);
      const our = Number(allocation.our_allocated_sum ?? 0);
      const profit = Number(
        allocation.profit_allocated_sum ?? profitByAllocation[allocationId] ?? 0
      );
      const supplier = Number(
        allocation.supplier_prepaid_sum ?? supplierByAllocation[allocationId] ?? 0
      );
      const used = our + profit + supplier;

      if (Math.abs(used - amount) > EPS) incompleteAllocations.push(allocation);
    });

    const paymentComplete = Math.abs(paymentRemaining) <= EPS;
    const allocationsComplete =
      allocations.length === 0
        ? moneySum <= EPS
        : incompleteAllocations.length === 0;

    return {
      complete: paymentComplete && allocationsComplete,
      paymentRemaining,
      incompleteCount: incompleteAllocations.length,
      allocationsCount: allocations.length,
    };
  };

  const openPayment = (id) => {
    navigate(`/accounting/${id}`);
  };

  // синхронизация activeTab с URL (back/forward, прямая ссылка)
  useEffect(() => {
    if (tabFromUrl !== activeTab) setActiveTab(tabFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromUrl]);

  // обёртка вместо setActiveTab: обновляет state + URL
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "accounting") {
      setSearchParams({});
    } else {
      setSearchParams({ tab });
    }
  };

  useEffect(() => {
    authFetch(API_URL)
      .then((response) => response.json())
      .then((data) => {
        const formattedRows = data.map((item) => {
          const allocationsText = formatAllocations(item.allocations);
          return {
            id: item.id,
            client: item.client,
            client_name: item.client_name,
            date: item.date || "",
            payment_agent: item.payment_agent || "",
            money_sum: item.money_sum ?? "",
            currency: item.currency,
            currency_code: item.currency_code,
            currency_symbol: item.currency_symbol,
            currency_name: item.currency_name,
            purpose: allocationsText || item.purpose || "",
            comment: item.comment || "",
            payment_status: item.payment_status || "",
            documents: item.documents || [],
            allocations: item.allocations || [],
            unallocated_sum: item.unallocated_sum,
            allocated_sum: item.allocated_sum,
            commission_allocations: item.commission_allocations || [],
          };
        });
        setRows(formattedRows);
      });

    authFetch(`${BASE_URL}/api/clients/`)
      .then((response) => response.json())
      .then((data) => {
        setClients(data);
      });

    authFetch(CURRENCIES_URL)
      .then((r) => r.json())
      .then((data) => setCurrencies(data))
      .catch((e) => console.error("currencies:", e));

    fetchAllPages(PROFIT_ALLOCATIONS_URL)
      .then(setProfitAllocations)
      .catch((err) => {
        console.error("profit allocations:", err);
        setDistributionError("Не удалось загрузить распределения в прибыль");
      });

    fetchAllPages(SUPPLIER_PREPAYMENTS_URL)
      .then(setSupplierPrepayments)
      .catch((err) => {
        console.error("supplier prepayments:", err);
        setDistributionError("Не удалось загрузить предоплаты поставщикам");
      });
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveRow = async () => {
    const payload = {
      client: Number(form.client),
      date: form.date || null,
      payment_agent: form.payment_agent,
      money_sum: form.money_sum,
      currency: form.currency ? Number(form.currency) : null,
      purpose: form.purpose,
      comment: form.comment,
      payment_status: form.payment_status,
    };

    try {
      const response = await authFetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        console.log("ERROR:", data);
        return;
      }
      setRows((prev) => [...prev, data]);
      setForm(emptyRow);
    } catch (error) {
      console.error(error);
    }
  };

  const editRow = (row) => {
    setEditingId(row.id);
    setEditingRow({ ...row });
  };

  const deleteRow = (row) => {
    setRowToDelete(row);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      const response = await authFetch(`${API_URL}${rowToDelete.id}/`, {
        method: "DELETE",
      });
      if (!response.ok) {
        console.log("DELETE ERROR");
        return;
      }
      setRows((prev) => prev.filter((row) => row.id !== rowToDelete.id));
      setShowDeleteConfirm(false);
      setRowToDelete(null);
    } catch (error) {
      console.error(error);
    }
  };

  const confirmEdit = async () => {
    if (!rowToEdit) return;

    const payload = {
      client: rowToEdit.client,
      date: rowToEdit.date,
      payment_agent: rowToEdit.payment_agent,
      money_sum: rowToEdit.money_sum,
      currency: rowToEdit.currency ? Number(rowToEdit.currency) : null,
      purpose: rowToEdit.purpose,
      comment: rowToEdit.comment,
      payment_status: rowToEdit.payment_status,
    };

    try {
      const response = await authFetch(`${API_URL}${rowToEdit.id}/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      
      const data = await response.json();
      if (!response.ok) {
        console.log(data);
        return;
      }
      setRows((prev) => prev.map((row) => (row.id === data.id ? data : row)));
      setEditingId(null);
      setRowToEdit(null);
      setShowEditConfirm(false);
    } catch (error) {
      console.error(error);
    }
  };

  const filteredRows = rows.filter((row) => {
    if (!searchValue) return true;
    const value = row[searchField];
    if (value === undefined) return false;
    return String(value).toLowerCase().includes(String(searchValue).toLowerCase());
  });

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4">Движение денежных средств</h2>

      <AccountingTabs active={activeTab} setActive={handleTabChange} />

      <div className="border rounded-b-lg rounded-tr-lg bg-white p-5">
        {activeTab === "accounting" && (
          <>
            <SearchPanel
              clients={clients}
              onSearch={(field, value) => {
                setSearchField(field);
                setSearchValue(value);
              }}
            />

            <table className="border-collapse border w-full text-sm mt-4">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2">Дата</th>
                  <th className="border p-2">Контрагент</th>
                  <th className="border p-2">Платежный агент</th>
                  <th className="border p-2">Сумма</th>
                  <th className="border p-2">Валюта</th>
                  <th className="border p-2">Назначение</th>
                  <th className="border p-2">Комментарий</th>
                  <th className="border p-2">Статус</th>
                  <th className="border p-2">Распределение</th>
                  <th className="border p-2">Подтверждающие документы</th>
                  <th className="border p-2">Действия</th>
                </tr>
              </thead>

              <tbody>
                {filteredRows.map((row) => {
                  const distribution = getPaymentDistribution(row);
                  return (
                  <tr
                    key={row.id}
                    onClick={() => {
                      if (editingId !== row.id) openPayment(row.id);
                    }}
                    className={`transition-colors duration-150 ${
                      editingId === row.id
                        ? "bg-white"
                        : distribution.complete
                        ? "cursor-pointer bg-emerald-50 hover:bg-emerald-100"
                        : "cursor-pointer bg-red-50 hover:bg-red-100"
                    }`}
                  >
                    <td className="border p-2">
                      {editingId === row.id ? (
                        <input
                          type="date"
                          value={row.date || ""}
                          onChange={(e) =>
                            setRows((prev) =>
                              prev.map((r) =>
                                r.id === row.id ? { ...r, date: e.target.value } : r
                              )
                            )
                          }
                        />
                      ) : (
                        row.date
                      )}
                    </td>

                    <td className="border p-2">
                      {editingId === row.id ? (
                        <select
                          value={row.client || ""}
                          onChange={(e) => {
                            const client = clients.find((c) => c.id === Number(e.target.value));
                            setRows((prev) =>
                              prev.map((r) =>
                                r.id === row.id
                                  ? {
                                      ...r,
                                      client: Number(e.target.value),
                                      client_name: client?.name || "",
                                    }
                                  : r
                              )
                            );
                          }}
                        >
                          <option value="">Выберите клиента</option>
                          {clients.map((client) => (
                            <option key={client.id} value={client.id}>
                              {client.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        row.client_name
                      )}
                    </td>

                    <td className="border p-2">
                      {editingId === row.id ? (
                        <input
                          value={row.payment_agent || ""}
                          onChange={(e) =>
                            setRows((prev) =>
                              prev.map((r) =>
                                r.id === row.id ? { ...r, payment_agent: e.target.value } : r
                              )
                            )
                          }
                        />
                      ) : (
                        row.payment_agent
                      )}
                    </td>

                    <td className="border p-2">
                      {editingId === row.id ? (
                        <input
                          type="number"
                          className={styles.noNumberSpinner}
                          value={row.money_sum || ""}
                          onWheel={(e) => e.currentTarget.blur()}
                          onChange={(e) => {
                            setRows((prev) =>
                              prev.map((r) =>
                                r.id === row.id ? { ...r, money_sum: e.target.value } : r
                              )
                            );
                          }}
                        />
                      ) : (
                        formatMoney(row.money_sum, currenciesById[row.currency])
                      )}
                    </td>

                    <td className="border p-2">
                      {editingId === row.id ? (
                        <select
                          value={row.currency || ""}
                          onChange={(e) => {
                            const value = e.target.value ? Number(e.target.value) : null;
                            const cur = value ? currenciesById[value] : null;
                            setRows((prev) =>
                              prev.map((r) =>
                                r.id === row.id
                                  ? {
                                      ...r,
                                      currency: value,
                                      currency_code: cur?.code ?? null,
                                      currency_symbol: cur?.symbol ?? null,
                                      currency_name: cur?.name ?? null,
                                    }
                                  : r
                              )
                            );
                          }}
                        >
                          <option value="">—</option>
                          {currencies.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.symbol} {c.code}
                            </option>
                          ))}
                        </select>
                      ) : (
                        row.currency_code || "—"
                      )}
                    </td>

                    <td className="border p-2">
                      {Array.isArray(row.allocations)
                        ? row.allocations
                            .map((a) => {
                              const code =
                                a.order_code ?? a.order?.code ?? a.order_number;
                              const amount =
                                a.money_sum ?? a.amount ?? a.allocated_amount;
                              return code
                                ? `${code}: ${formatMoney(
                                    amount,
                                    currenciesById[a.currency] || currenciesById[row.currency]
                                  )}`
                                : null;
                            })
                            .filter(Boolean)
                            .join("; ")
                        : ""}
                    </td>

                    <td className="border p-2">
                      {editingId === row.id ? (
                        <input
                          value={row.comment || ""}
                          onChange={(e) => {
                            setRows((prev) =>
                              prev.map((r) =>
                                r.id === row.id ? { ...r, comment: e.target.value } : r
                              )
                            );
                          }}
                        />
                      ) : (
                        row.comment
                      )}
                    </td>


                    <td className="border p-2">
                      {editingId === row.id ? (
                        <select
                          value={row.payment_status || ""}
                          onChange={(e) => {
                            setRows((prev) =>
                              prev.map((r) =>
                                r.id === row.id
                                  ? { ...r, payment_status: e.target.value }
                                  : r
                              )
                            );
                          }}
                        >
                          <option value="PENDING">Ожидает оплаты</option>
                          <option value="PAID">Оплачен</option>
                        </select>
                      ) : row.payment_status === "PAID" ? (
                        "Оплачен"
                      ) : (
                        "Ожидает оплаты"
                      )}
                    </td>

                    <td className="border p-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                          distribution.complete
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-700"
                        }`}
                        title={
                          distribution.complete
                            ? "Платёж и все клиентские назначения распределены полностью"
                            : [
                                distribution.paymentRemaining > EPS
                                  ? `Остаток платежа: ${formatMoney(
                                      distribution.paymentRemaining,
                                      currenciesById[row.currency]
                                    )}`
                                  : null,
                                distribution.incompleteCount > 0
                                  ? `Не полностью распределено назначений: ${distribution.incompleteCount}`
                                  : null,
                                distribution.allocationsCount === 0
                                  ? "Нет клиентских назначений"
                                  : null,
                              ]
                                .filter(Boolean)
                                .join("\n")
                        }
                      >
                        {distribution.complete ? "✓ Распределён" : "✕ Не распределён"}
                      </span>
                    </td>

                    <td className="border p-2">
                      <button
                        type="button"
                        className="text-blue-700 underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDocumentsPayment(row);
                        }}
                      >
                        Документы ({row.documents?.length || 0})
                      </button>
                    </td>


                    <td className="border p-2">
                      <button
                        className="text-blue-600 mr-3"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (editingId === row.id) {
                            setRowToEdit(row);
                            setShowEditConfirm(true);
                          } else {
                            editRow(row);
                          }
                        }}
                      >
                        {editingId === row.id ? "Сохранить" : "Редактировать"}
                      </button>

                      <button
                        className="text-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteRow(row);
                        }}
                      >
                        Удалить
                      </button>
                    </td>
                  </tr>
                  );
                })}

                {/* Строка добавления */}
                <tr>
                  <td className="border p-1">
                    <input
                      type="date"
                      value={form.date}
                      onChange={(e) => handleChange("date", e.target.value)}
                    />
                  </td>

                  <td className="border p-1">
                    <select
                      value={form.client}
                      onChange={(e) => handleChange("client", e.target.value)}
                    >
                      <option value="">Выберите клиента</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td className="border p-1">
                    <input
                      value={form.payment_agent}
                      onChange={(e) => handleChange("payment_agent", e.target.value)}
                    />
                  </td>

                  <td className="border p-1">
                    <input
                      type="number"
                      className={styles.noNumberSpinner}
                      onWheel={(e) => e.currentTarget.blur()}
                      value={form.money_sum}
                      onChange={(e) => handleChange("money_sum", e.target.value)}
                    />
                  </td>

                  <td className="border p-1">
                    <select
                      value={form.currency}
                      onChange={(e) => handleChange("currency", e.target.value)}
                    >
                      <option value="">—</option>
                      {currencies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.symbol} {c.code}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td className="border p-1">
                    <input
                      value={form.purpose}
                      onChange={(e) => handleChange("purpose", e.target.value)}
                    />
                  </td>

                  <td className="border p-1">
                    <input
                      value={form.comment}
                      onChange={(e) => handleChange("comment", e.target.value)}
                    />
                  </td>

                  <td className="border p-1">
                    <select
                      value={form.payment_status}
                      onChange={(e) => handleChange("payment_status", e.target.value)}
                    >
                      <option value="PENDING">Ожидает оплаты</option>
                      <option value="PAID">Оплачен</option>
                    </select>
                  </td>

                  <td className="border p-1" colSpan={3}>
                    <button
                      className="bg-blue-600 text-white px-3 py-1 rounded"
                      onClick={saveRow}
                    >
                      Добавить
                    </button>
                  </td>

                </tr>
              </tbody>
            </table>
          </>
        )}

        {activeTab === "accounts" && <AccountsTab />}

        {activeTab === "currencies" && (
          <CurrenciesTab currencies={currencies} setCurrencies={setCurrencies} />
        )}

        {activeTab === "finance-report" && <FinanceReportTab />}

        {activeTab === "agents" && (
          <div className="h-96 flex items-center justify-center text-gray-400">
            Раздел "Список платежных агентов"
          </div>
        )}
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 w-96">
            <h3 className="text-lg font-bold mb-4">Удалить запись?</h3>
            <p className="mb-6 text-gray-600">
              Клиент: {rowToDelete?.client_name}
              <br />
              Сумма: {formatMoney(rowToDelete?.money_sum, currenciesById[rowToDelete?.currency])}
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 border rounded"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setRowToDelete(null);
                }}
              >
                Отмена
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded"
                onClick={confirmDelete}
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 w-96">
            <h3 className="text-lg font-bold mb-4">Сохранить изменения?</h3>
            <p className="mb-6 text-gray-600">
              Клиент: {rowToEdit?.client_name}
              <br />
              Сумма: {formatMoney(rowToEdit?.money_sum, currenciesById[rowToEdit?.currency])}
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 border rounded"
                onClick={() => {
                  setShowEditConfirm(false);
                  setRowToEdit(null);
                }}
              >
                Отмена
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded"
                onClick={confirmEdit}
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}

      {documentsPayment && (
        <PaymentDocumentsModal
          payment={documentsPayment}
          onClose={() => setDocumentsPayment(null)}
          onDocumentsChanged={(paymentId, documents) => {
            setRows((prev) =>
              prev.map((row) =>
                row.id === paymentId ? { ...row, documents } : row
              )
            );
            setDocumentsPayment((prev) =>
              prev?.id === paymentId ? { ...prev, documents } : prev
            );
          }}
        />
      )}
    </div>
  );
}